import type { ContentSummary } from '@augment-toolkit/core';

/**
 * Reading the catalog for the relationships the per-page endpoints do not return.
 *
 * **Matched on `path` rather than `parentId`, because a summary has no
 * `parentId`** — and the path *is* the hierarchy. A child is anything whose path
 * starts with the parent's plus a separator, and **the separator is what stops
 * `user-research-ops` matching `user-research`.**
 */

/**
 * Every method in the catalog, grouped by the path of the process above it.
 *
 * **Built once per render of the page that needs it**, rather than filtered per
 * card: twelve process cards each scanning five hundred records is six thousand
 * comparisons for a page that could do five hundred.
 *
 * Methods keep the catalog's order, which is depth-first tree order — so a
 * process's methods arrive together and already sorted.
 */
export const methodsByProcess = (catalog: ContentSummary[]): Map<string, ContentSummary[]> => {
  const grouped = new Map<string, ContentSummary[]>();

  for (const item of catalog) {
    if (item.type !== 'Method') {
      continue;
    }

    const separator = item.path.lastIndexOf('/');

    if (separator === -1) {
      continue;
    }

    const processPath = item.path.slice(0, separator);
    const existing = grouped.get(processPath);

    if (existing === undefined) {
      grouped.set(processPath, [item]);
    } else {
      existing.push(item);
    }
  }

  return grouped;
};

/**
 * The other methods under the same process, excluding the one being read — what
 * the method page's "Nearby in" row is built from. Empty when a method is the
 * only one under its process, which is real and renders as no row rather than
 * as an empty heading.
 */
export const nearbyMethods = (
  catalog: ContentSummary[],
  methodPath: string,
): ContentSummary[] => {
  const separator = methodPath.lastIndexOf('/');

  if (separator === -1) {
    return [];
  }

  const siblings = methodsByProcess(catalog).get(methodPath.slice(0, separator)) ?? [];

  return siblings.filter((method) => method.path !== methodPath);
};
